import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { Autocomplete, Button, Form, Header, Modal, ModalSize, RadioCheckButtons, SleekInput } from "@kaladel/react/Components";
import { useValidator } from "@kaladel/react/Hooks";
import { FC, useState } from "react";
import { CreditCardPaymentBehaviour, CreditCardPaymentBehaviourObject, StaticCurrency } from "ApiClient/models";
import { classNames, Severity } from "@kaladel/react/Utilities";
import { createPortal } from "react-dom";
import modalStyles from "./ModalStyles.module.scss";
import { useApp } from "../AppContext";
import { useApiClient } from "Contexts/ApiClientContext";

interface AddCreditCardModalProps {
    onClose: (dirty: boolean, newId?: string) => void;
}

export const AddCreditCardModal: FC<AddCreditCardModalProps> = (props) => {
    const { apiCall, api } = useApiClient();
    const { currencies } = useApp();
    const validator = useValidator();
    const lastStage = 5;

    const [stage, setStage] = useState<number>(1);

    const [name, setName] = useState<string>("");
    const [currencyId, setCurrencyId] = useState<string>("");
    const [currencyName, setCurrencyName] = useState<string>("");
    const [balance, setBalance] = useState<string>("");
    const [creditLimit, setCreditLimit] = useState<string>("0");
    const [paymentBehaviour, setPaymentBehaviour] = useState<string[]>([CreditCardPaymentBehaviourObject.MainBalanceOnly]);

    const handleBackClicked = () => {
        if (stage !== 1) {
            const nextStage = stage - 1;

            setStage(nextStage);
        }
        else {
            props.onClose(false);
        }
    };

    const handleNextClicked = () => {
        if (!validator.canSubmit()) return;

        if (stage !== lastStage) {
            const nextStage = stage + 1;

            setStage(nextStage);

            validator.reset();
        }
        else {
            saveChanges();
        }
    };

    const getCurrencyAutocompleteResults = async (value: string): Promise<StaticCurrency[]> => {
        if (!currencies?.length) return [];

        return currencies.filter(c => c.code.toLowerCase().includes(value.toLowerCase()) || c.friendlyName.toLowerCase().includes(value.toLowerCase()));
    };

    const handleCurrencySelected = (currency: StaticCurrency) => {
        setCurrencyId(currency.code);
        setCurrencyName(`${currency.code} - ${currency.friendlyName}`);
    };

    const saveChanges = async () => {
        if (!validator.canSubmit()) return;

        const response = await apiCall(() => api.creditcards.post({
            name,
            currencyId,
            balance: parseFloat(balance),
            creditLimit: parseFloat(creditLimit),
            paymentBehaviour: paymentBehaviour[0] as CreditCardPaymentBehaviour
        }));

        if (response.kind === "success") {
            props.onClose(true, response.value);
        }
    };

    return (
        createPortal(
            <Modal
                onClose={() => props.onClose(false)}
                size={ModalSize.Medium}
            >
                <Header level={2} title="Add Credit Card" />

                <Form>

                    {stage === 1 && (
                        <>
                            <div>Give your credit card a name</div>

                            <SleekInput
                                id="name"
                                label="Name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                validator={validator}
                                validations={{ required: true }}
                                autoFocus={true}
                            />
                        </>
                    )}

                    {stage === 2 && (
                        <>
                            <div>What currency is the credit card in?</div>

                            <Autocomplete<StaticCurrency>
                                autocompleteOptionKey={c => c.code}
                                autocompleteDisplayOption={c => `${c.code} - ${c.friendlyName}`}
                                onAutocomplete={getCurrencyAutocompleteResults}
                                onAutocompleteSelected={handleCurrencySelected}
                                value={currencyName}
                                label="Currency"
                                id="currencyId"
                                validator={validator}
                                validations={{ required: true }}
                                autoFocus={true}
                            />
                        </>
                    )}

                    {stage === 3 && (
                        <>
                            <div>What is the current balance?</div>

                            <SleekInput
                                id="balance"
                                label="Balance"
                                value={balance}
                                onChange={e => setBalance(e.target.value)}
                                validator={validator}
                                validations={{ required: true, numeric: true }}
                                autoFocus={true}
                            />
                        </>
                    )}

                    {stage === 4 && (
                        <>
                            <div>What is the credit limit?</div>

                            <SleekInput
                                id="creditLimit"
                                label="Credit Limit"
                                value={creditLimit}
                                onChange={e => setCreditLimit(e.target.value)}
                                validator={validator}
                                validations={{ required: true, numeric: true }}
                                autoFocus={true}
                            />
                        </>
                    )}

                    {stage === 5 && (
                        <>
                            <div>When a payment is made, should we automatically apply it to promotional balances?</div>

                            <RadioCheckButtons
                                uniform={true}
                                items={[{ id: CreditCardPaymentBehaviourObject.MainBalanceOnly, name: "No" }, { id: CreditCardPaymentBehaviourObject.UpdatePromotionalBalances, name: "Yes" }]}
                                id="paymentBehaviour"
                                values={i => i.id.toString()}
                                descriptions={i => i.name}
                                onChange={v => setPaymentBehaviour(v)}
                                value={paymentBehaviour}
                                validator={validator}
                                validations={{ required: true }}
                            />
                        </>
                    )}

                    <div className={classNames([modalStyles.buttons, modalStyles.right])}>
                        <Button
                            onClick={handleBackClicked}
                            icon={faChevronLeft}
                            iconSide="left"
                            severity={Severity.Tertiary}
                        >
                            {stage !== 1 ? "Back" : "Cancel"}
                        </Button>

                        <Button
                            onClick={handleNextClicked}
                            icon={faChevronRight}
                            iconSide="right"
                            type="submit"
                        >
                            {stage !== lastStage ? "Next" : "Finish"}
                        </Button>
                    </div>
                </Form>

            </Modal>,
            document.body
        )
    );
};